import React, { useState, useMemo } from 'react';
import {
    Typography,
    useTheme,
    Card,
    CardContent,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    Button,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { K8sTimeSeriesDataPoint } from './KubernetesAnalysis';
import { getColorForString } from '../../utils/colorUtils';

// --- Error/Empty State Display Component ---
const ErrorDisplay = ({ onRefresh }: { onRefresh: () => void; }) => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
        height: 300, // Match chart height
    }}>
        <img src="/usmerror.svg" alt="Error" style={{ width: '130px', marginBottom: '16px' }} />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Unable to Display Data
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '300px', mb: 2 }}>
            We're having trouble loading chart data right now. Please try again.
        </Typography>
        <Button
            variant="text"
            onClick={onRefresh}
            sx={{
                textTransform: 'none',
                color: '#0064FF',
                fontWeight: 500,
                fontFamily: 'Inter',
                lineHeight: '21px',
                fontSize: '14px',
            }}
        >
            Refresh Page
        </Button>
    </Box>
);

// --- Type Definition ---
interface CostDistributionChartProps {
    costOverTimeData: K8sTimeSeriesDataPoint[];
    loading: boolean;
    error?: Error | null;
    onRefresh?: () => void;
    colorMap: { [key: string]: string };
}

const CostDistributionChart: React.FC<CostDistributionChartProps> = ({ costOverTimeData, loading, error, onRefresh, colorMap }) => {
    const theme = useTheme();
    const [view, setView] = useState('bar');

    const chartOption = useMemo(() => {
        if (!costOverTimeData || costOverTimeData.length === 0) {
            return {};
        }

        const allGroupNames = Array.from(new Set(costOverTimeData.flatMap(d => d.groups.map(g => g.name))));

        const dates = costOverTimeData.map(dp => dp.date);

        const seriesData = new Map<string, (number | null)[]>();
        allGroupNames.forEach(group => seriesData.set(group, new Array(dates.length).fill(0)));

        let maxDailyTotal = 0;
        costOverTimeData.forEach(dp => {
            let dailyTotal = 0;
            dp.groups.forEach(g => { dailyTotal += g.cost; });
            if (dailyTotal > maxDailyTotal) {
                maxDailyTotal = dailyTotal;
            }
        });
        const niceMaxValue = maxDailyTotal > 0 ? Math.ceil(maxDailyTotal / 0.85) : 1000;

        const placeholderData = new Array(dates.length).fill(null);
        costOverTimeData.forEach((dataPoint, dateIndex) => {
            let dailyTotal = 0;
            dataPoint.groups.forEach(group => {
                dailyTotal += group.cost;
                const currentData = seriesData.get(group.name);
                if (currentData) {
                    (currentData[dateIndex] as number) += group.cost;
                }
            });

            if (dailyTotal === 0 && view === 'bar') {
                const minHeight = niceMaxValue * 0.50;
                const maxHeight = niceMaxValue * 0.85;
                placeholderData[dateIndex] = Math.random() * (maxHeight - minHeight) + minHeight;
            }
        });

        const series = allGroupNames.map((groupName, index) => ({
            name: groupName,
            type: view,
            stack: 'total',
            // Use the passed-in colorMap, with a fallback just in case
            color: colorMap[groupName] || getColorForString(groupName),
            emphasis: { focus: 'series' },
            data: seriesData.get(groupName) || [],
            barMaxWidth: 12,
             ...(view === 'line' && {
                showSymbol: false,
            })
        }));

        if (view === 'line') {
            series.forEach(s => {
                Object.assign(s, {
                    type: 'line',
                    showSymbol: false,
                    emphasis: {
                        ...s.emphasis,
                        itemStyle: { opacity: 1 }
                    }
                });
            });
        }

        if (view === 'bar' && placeholderData.some(val => val !== null)) {
            series.unshift({
                name: 'placeholder',
                type: 'bar',
                stack: 'total',
                itemStyle: {
                    color: theme.palette.grey[200],
                },
                data: placeholderData,
                barMaxWidth: 12,
            } as any);
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any[]) => {
                    const relevantParams = params.filter(p => p.seriesName !== 'placeholder' && p.value > 0);
                    if (relevantParams.length === 0) return '';

                    const dateLabel = params[0].axisValue;
                    const dateObject = dayjs(dateLabel);

                    const title = `${dateObject.format('dddd, MMMM D')} • ${dateObject.year()}`;

                    const content = relevantParams
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 5)
                        .map(p => `
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
                                <div style="display: flex; align-items: center;">
                                    <span style="font-size: 10px; color: ${p.color}; margin-right: 6px;">●</span>
                                    <span style="font-size: 12px; font-weight: 500;">${p.seriesName}</span>
                                </div>
                                <span style="font-size: 12px; font-weight: 500;">$${(p.value as number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        `).join('');

                    return `
                        <div style="font-family: Inter; border-radius: 8px; padding: 12px; min-width: 220px; background-color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                            <div style="font-size: 12px; color: #888; margin-bottom: 12px; text-align: center;">${title}</div>
                            ${content}
                        </div>
                    `;
                },
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                shadowColor: 'transparent',
                padding: 0,
            },
            
            grid: { left: '1%', right: '1%', bottom: '0%', top: '20px', containLabel: true },
            xAxis: { type: 'category', data: dates, show: false },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => (value >= 1000 ? `$${value / 1000}K` : `$${value}`),
                    fontFamily: 'Inter',
                    color: '#888',
                    inside: false
                },
                splitLine: { lineStyle: { type: 'dashed' } },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series,
            animation: true,
        };
    }, [view, theme, costOverTimeData, colorMap]);

    const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
        if (newView !== null) {
            setView(newView);
        }
    };

    const buttonSx = {
        borderRadius: '8px !important',
        border: 'none',
        px: 1.2,
        py: 1.2,
        '& .MuiSvgIcon-root': {
            color: theme.palette.text.secondary,
            fontSize: '18px',
        },
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            '& .MuiSvgIcon-root': {
                color: theme.palette.primary.main,
            },
        },
    };

    const showErrorState = error || (!loading && (!costOverTimeData || costOverTimeData.length === 0));

    return (
        <Card elevation={0} sx={{ borderRadius: '16px', fontFamily: 'Inter, sans-serif', mt: 2 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: '550', fontSize: '18px' }}>
                        Cost Distribution Over Time
                    </Typography>
                    {!showErrorState && (
                        <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} aria-label="chart view" sx={{ backgroundColor: theme.palette.grey[100], p: '3px', borderRadius: '12px' }}>
                            <ToggleButton value="bar" aria-label="bar chart view" sx={buttonSx}><BarChartIcon /></ToggleButton>
                            <ToggleButton value="line" aria-label="line chart view" sx={buttonSx}><ShowChartIcon /></ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                        <CircularProgress />
                    </Box>
                ) : showErrorState ? (
                    <ErrorDisplay onRefresh={onRefresh || (() => {})} />
                ) : (
                    <Box sx={{ height: 300, width: '100%' }}>
                        <ReactECharts
                            option={chartOption}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default CostDistributionChart;